import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { EntityGrid, Typography, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './pinned-section.module.css'

/**
 * Dashboard Phase 2 — top shelf for the user's pinned entities, rendered as cards.
 *
 * Source: `userConfig.favorites` entries flagged `pinned: true` (max 3 enforced by the
 * pin API). Resolves `entityId` → `Persisted<Entity>` via `useEntitiesById`. Missing
 * or trashed entities are dropped silently — the user sees an honest view of what's
 * still reachable, not a list of broken links.
 *
 * Pin order is honoured at render time. Drag-reorder for the cards layout is a
 * v1.x follow-up; with max 3 pins the practical loss is small and users can
 * unpin + re-pin to change order in the meantime.
 *
 * The section renders nothing (no header, no skeleton) when there is nothing to show
 * so it doesn't take dashboard real estate on accounts with no pins.
 */
export const PinnedSection: FC = () => {
  const { pinnedFavorites } = useFavorites()
  const navigateToEntity = useNavigateToEntity()

  const pinnedIds = pinnedFavorites
    .map(f => f.entityId)
    .filter((id): id is string => typeof id === 'string')

  const { existingEntities = [] } = graphHooks.useEntitiesById(pinnedIds)

  // useEntitiesById returns whatever order the cache gives us — re-impose pinOrder so
  // the visible cards match the user's most recent pin sequence.
  const indexById = new Map(existingEntities.map(e => [e.id, e]))
  const orderedEntities = pinnedIds
    .map(id => indexById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)

  if (orderedEntities.length === 0) {
    return null
  }

  return (
    <section className={styles.section} aria-labelledby="pinned-heading">
      <Typography id="pinned-heading" className={styles.heading} variant="h2">
        Pinned
      </Typography>
      <EntityGrid size="medium" entities={orderedEntities} onOpen={navigateToEntity} showItemHeaderAction />
    </section>
  )
}

export default PinnedSection
